import { createRootRoute, createRoute, createRouter, Outlet, RouterProvider } from '@tanstack/react-router';
import { ConfirmPage } from './pages/confirm';
import { DurationPage } from './pages/duration';
import { ExtendConfirmPage, ExtendPage } from './pages/extend';
import { HomePage } from './pages/home';
import { LoginPage } from './pages/login';
import { LotPage } from './pages/lot';
import { LotLandingPage } from './pages/lot-landing';
import { PayPage, PayReturnPage } from './pages/pay';
import { PlatesPage } from './pages/plates';
import { HistoryPage, ReceiptPage } from './pages/records';
import { SessionPage, SuccessPage } from './pages/session';

const rootRoute = createRootRoute({ component: Outlet });

const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: HomePage });

/** The URL printed on the lot's QR sticker. */
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/l/$code',
  component: LotLandingPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    next: typeof search.next === 'string' ? search.next : undefined,
  }),
});

const lotRoute = createRoute({ getParentRoute: () => rootRoute, path: '/lot/$lotId', component: LotPage });

const platesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lot/$lotId/plates',
  component: PlatesPage,
});

const durationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lot/$lotId/duration',
  component: DurationPage,
  validateSearch: (search: Record<string, unknown>) => ({
    vehicleId: String(search.vehicleId ?? ''),
    spaceNumber: String(search.spaceNumber ?? ''),
  }),
});

const confirmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lot/$lotId/confirm',
  component: ConfirmPage,
  validateSearch: (search: Record<string, unknown>) => ({
    quoteId: String(search.quoteId ?? ''),
  }),
});

/**
 * Fallback only: the success page prefers `Payment.kind` from the API
 * (backend bc82c4c). The quote's `kind` still rides the URL from confirm
 * through /pay, the provider redirect and /success because the deployed API
 * may predate the field and in-flight bank redirects already carry it; retire
 * this threading once neither holds. Anything but the literal 'extension'
 * must read as a first payment — the success page may never infer the
 * variant from amounts.
 */
const quoteKind = (search: Record<string, unknown>): 'new' | 'extension' =>
  search.kind === 'extension' ? 'extension' : 'new';

const payRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pay',
  component: PayPage,
  validateSearch: (search: Record<string, unknown>) => ({
    quoteId: String(search.quoteId ?? ''),
    kind: quoteKind(search),
  }),
});

const payReturnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pay/return',
  component: PayReturnPage,
  validateSearch: (search: Record<string, unknown>) => ({
    paymentId: String(search.paymentId ?? ''),
    kind: quoteKind(search),
    // Optional: lets the return page fall back to a same-quote /pay restart
    // for "change method" — the reused idempotency key makes that re-entry safe.
    quoteId: typeof search.quoteId === 'string' ? search.quoteId : undefined,
  }),
});

const successRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/success',
  component: SuccessPage,
  validateSearch: (search: Record<string, unknown>) => ({
    sessionId: String(search.sessionId ?? ''),
    paymentId: typeof search.paymentId === 'string' ? search.paymentId : undefined,
    kind: quoteKind(search),
  }),
});

const sessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sessions/$sessionId',
  component: SessionPage,
});

const extendRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sessions/$sessionId/extend',
  component: ExtendPage,
});

const extendConfirmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sessions/$sessionId/extend-confirm',
  component: ExtendConfirmPage,
  validateSearch: (search: Record<string, unknown>) => ({ quoteId: String(search.quoteId ?? '') }),
});

const historyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/history',
  component: HistoryPage,
});

const receiptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/receipt/$sessionId',
  component: ReceiptPage,
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  landingRoute,
  loginRoute,
  lotRoute,
  platesRoute,
  durationRoute,
  confirmRoute,
  payRoute,
  payReturnRoute,
  successRoute,
  sessionRoute,
  extendRoute,
  extendConfirmRoute,
  historyRoute,
  receiptRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return <RouterProvider router={router} />;
}
